import React, { useEffect, useRef } from 'react';
import { Step } from 'types';
import { Download, Eye, X } from '@phosphor-icons/react';
import { FileText, ShoppingCartSimple, Wallet } from '@phosphor-icons/react';
import { ButtonCommon, LayoutTwoColComponent, LoadingFallback, StepComponent } from 'components/common';
import { formatPrice, getFilePath } from 'utils';
import { ReturnConfirmPanel } from 'components/ReturnOrder';
import { SimpleProductItem } from 'components/Product';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useGetReturnOrderByIdQuery } from 'services/returnOrder.service';
import { Form } from 'antd';
import { useProductImagesMap } from 'hooks/useProductImagesMap';

const ReturnOrderPage: React.FC = () => {
  const isDesktop = false;
  const { id } = useParams<{ id: string }>();
  const currentStepRef = useRef<number>(0);
  const [form] = Form.useForm();
  const [isCartPanelVisible, setIsCartPanelVisible] = React.useState(false);
  const [formKey, setFormKey] = React.useState(0);
  const navigate = useNavigate();
  const steps: Step[] = [
    {
      title: 'Chọn Sản Phẩm',
      icon: <ShoppingCartSimple size={20} />,
    },
    {
      title: 'Nhập Thông Tin Đơn Trả',
      icon: <FileText size={20} />,
    },
    {
      title: 'Xác Nhận Đơn Trả',
      icon: <Wallet size={20} />,
      isCompleted: true,
    },
  ];
  const { data, isLoading } = useGetReturnOrderByIdQuery({
    id: id || '',
    query: {
      include: 'inventoryDocumentItems|supplier.baseUser',
      includeRef: 'Debt',
    },
  });
  const inventoryDocument = data?.data;
  const inventoryDocumentItems = inventoryDocument?.inventoryDocumentItems || [];
  const totalQuantity = inventoryDocumentItems.reduce((acc, item) => acc + item.quantity, 0) || 0;
  const totalPrice = inventoryDocumentItems.reduce((acc, item) => acc + item.costPrice * item.quantity, 0) || 0;
  const toggleCartPanel = () => {
    setIsCartPanelVisible((prev) => !prev);
  };
  const productVersionIds = inventoryDocumentItems.map((item) => item.productVersionId);
  // const [productItems, setProductItems] = useState<ProductData[]>([]);
  const { imagesMap: productImagesMap } = useProductImagesMap(productVersionIds);
  const getImageUrl = (productVersionId: string) => {
    const imagePaths = productImagesMap?.[productVersionId];
    return imagePaths && imagePaths.length > 0 ? getFilePath() + '/' + imagePaths[0] : undefined;
  };
  useEffect(() => {
    if (data?.data) {
      form.setFieldValue('userName', data?.data?.userName);
      form.setFieldValue('code', data?.data?.code);
      form.setFieldValue('barcode', data?.data?.barcode);
      form.setFieldValue('email', data?.data?.userEmail);
      form.setFieldValue('phoneNumber', data?.data?.userPhoneNumber);
      form.setFieldValue('supplierName', data?.data?.supplier?.baseUser.name);
      form.setFieldValue('supplierEmail', data?.data?.supplier?.baseUser?.email);
      form.setFieldValue('supplierPhoneNumber', data?.data?.supplier?.baseUser?.phoneNumber);
      form.setFieldValue('supplierAddress', data?.data?.supplier?.baseUser?.address);
      form.setFieldValue('describeNote', data?.data?.note);
      form.setFieldValue('describeOrder', data?.data?.description);
      form.setFieldValue('deliveryDate', data?.data?.deliveryDate);
      setFormKey((prev) => prev + 1);
    }
  }, [data, inventoryDocument, form]);

  const rightComponent = (currentStep: number) => {
    switch (currentStep) {
      case 2:
        return (
          <div className='w-[444px] flex flex-col h-full p-6 overflow-auto bg-white rounded-[16px] '>
            <div className='text-[20px] font-bold mb-4'>THÔNG TIN & GIÁ TRỊ SẢN PHẨM</div>

            <div className='flex-1 flex flex-col gap-2 w-full items-center overflow-y-auto '>
              {inventoryDocumentItems.length > 0 ? (
                inventoryDocumentItems.map((item) => (
                  <SimpleProductItem
                    key={item.id}
                    name={
                      item.name + (item.color ? ` - ${item.color}` : '') + (item.sizeSpec ? ` - ${item.sizeSpec}` : '')
                    }
                    price={item.costPrice}
                    imageUrl={item.productVersionId ? getImageUrl(item.productVersionId) : undefined}
                  />
                ))
              ) : (
                <p className='text-black/60'>Không có sản phẩm</p>
              )}
            </div>

            <div className='flex flex-col'>
              <hr className='my-4' />
              <div className='flex flex-col gap-3 text-sm leading-tight'>
                <div className='grid grid-cols-4 items-center gap-2'>
                  <span className='text-black/60 font-normal'>Số lượng</span>
                  <span className='text-black/90 font-medium col-span-3'>
                    {formatPrice(totalQuantity)} <span className='opacity-50 ml-2'> sản phẩm</span>
                  </span>
                </div>
                <div className='grid grid-cols-4 items-center gap-2'>
                  <span className='text-black/60 font-normal'>Tạm tính</span>
                  <span className='text-black/90 font-bold col-span-3'>
                    {formatPrice(totalPrice)}
                    <span className='opacity-50'> VND</span>
                  </span>
                </div>
                <span className='text-sm col-span-4 text-[var(--sematicErrorColor500)]'>
                  Số tiền còn lại được chuyển về công nợ{' '}
                  <span
                    className='font-bold cursor-pointer '
                    onClick={() => navigate(`/management/debts?type=SUPPLIER&id=${inventoryDocument?.Debt?.[0]?.id}`)}
                  >
                    {inventoryDocument?.Debt?.[0]?.code}
                  </span>
                </span>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };
  if (isLoading || !data?.data) {
    return <LoadingFallback message='Đang tải ...' subMessage='Vui lòng chờ...' />;
  }
  return (
    <>
      <StepComponent steps={steps} form={form} initialStep={2}>
        {({ currentStep }) => {
          currentStepRef.current = currentStep;
          const renderLeftComponent = () => {
            switch (currentStep) {
              case 2:
                return (
                  <>
                    <ReturnConfirmPanel key={formKey} form={form} />
                  </>
                );
              default:
                return null;
            }
          };
          const renderRightComponent = () => {
            if (!isDesktop) {
              return null;
            }
            return rightComponent(currentStep);
          };
          return (
            <>
              <LayoutTwoColComponent
                currentStep={currentStep}
                statusLabel='Đã tạo đơn'
                createOrderSupplier={data?.data?.createdAt as unknown as string}
                statusColor='var(--sematicSuccessColor500)'
                form={form}
                steps={steps}
                cancelButton={false}
                left={<div className='w-full h-full'>{renderLeftComponent()}</div>}
                right={renderRightComponent()}
                rightClassName={`mt-2 min-h-max ${currentStep === 0 ? 'max-w-[0px]' : 'xl:max-w-[444px] max-w-[400px]'}`}
                leftClassName='overflow-x-hidden'
                customButton={
                  <>
                    <Link to={'/management/inventory/return-orders'}>
                      <ButtonCommon color='none' radius='rounded'>
                        Thoát
                      </ButtonCommon>
                    </Link>
                    <ButtonCommon color='soft' radius='rounded' size='sm' icon={<Download size={20} />}>
                      Xuất tệp Excel
                    </ButtonCommon>
                    <ButtonCommon color='soft' radius='rounded' size='sm' icon={<Eye size={20} />}>
                      Xem công nợ
                    </ButtonCommon>
                  </>
                }
              />
            </>
          );
        }}
      </StepComponent>
      {!isDesktop && (
        <div className='fixed bottom-6 right-6 z-40 cursor-pointer' onClick={toggleCartPanel}>
          <div className='bg-[var(--primaryColor500)] text-white rounded-full w-16 h-16 flex items-center justify-center shadow-lg'>
            <ShoppingCartSimple size={28} weight='fill' />
            {inventoryDocumentItems && inventoryDocumentItems.length > 0 && (
              <div className='absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold'>
                {inventoryDocumentItems.length}
              </div>
            )}
          </div>
        </div>
      )}

      {!isDesktop && (
        <div className={`fixed inset-0 z-50 flex justify-end ${isCartPanelVisible ? 'visible' : 'invisible'}`}>
          <div
            className={`absolute inset-0 bg-black transition-opacity duration-300 ${
              isCartPanelVisible ? 'opacity-50' : 'opacity-0'
            }`}
            onClick={() => setIsCartPanelVisible(false)}
          ></div>

          <div
            className={`relative bg-white min-w-[500px] flex flex-col max-w-[550px] h-full transform transition-transform duration-300 ease-out shadow-xl overflow-auto ${
              isCartPanelVisible ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div className='sticky top-0 bg-white p-4 border-b border-gray-200 flex items-center justify-between z-10'>
              <div className='text-lg font-bold'></div>
              <button
                className='text-gray-500 hover:text-gray-700 focus:outline-none'
                onClick={() => setIsCartPanelVisible(false)}
              >
                <X size={24} />
              </button>
            </div>
            <div className='p-4 flex-1'>{rightComponent(currentStepRef.current)}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReturnOrderPage;
